import { useRef } from 'react';
import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import { RecorderPopout } from './Recorder';
import { useScrollFade } from './useScrollFade';
import './HomeView.css';

/**
 * Tab 3's content — the app's HOME page, not a meeting.
 *
 * Structure follows the real page: a greeting with the open-task count, then
 * "Up next" (one hero row plus a queue, per UpNextGroupedList's HeroRow/QueueRow
 * split) and "Needs attention" (checkbox, action text, source line, due cell,
 * per NeedsAttentionPanel).
 *
 * ⚠️ EVERY value here is INVENTED. The owner's reference screenshots carry real
 * data — a real account name and address, a client's phone number, a live
 * project code, and Arabic action items from an actual meeting. None of it may
 * appear on a public marketing page, so only the SHAPE was taken. The persona is
 * the same one AppShell's chrome already uses, so the demo reads as one account.
 *
 * The AR side is a full translation of the page rather than a code-switch.
 * Mixed script belongs to tab 2, where mid-sentence switching is the actual
 * claim being made; here it would just look like an untranslated screen.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide workflow, step 7).
 */

interface Upcoming {
  time: string;
  when: string;
  title: string;
  sub: string;
  join?: boolean;
}

interface HomeCopy {
  greeting: string;
  tasks: (n: number) => string;
  upNext: string;
  needsAttention: string;
  seeAll: string;
  open: string;
  /* The whole label, arrow included, because the arrow is DIRECTIONAL and a
     forward arrow that does not mirror is the §13 failure family — it passes
     review in one locale and points backwards in the other. Storing the glyph
     with its text is what makes the mirroring impossible to forget. */
  openLink: string;
  join: string;
  /** Split rather than sliced — see HERO_ROW. */
  heroRow: Upcoming;
  queueRows: readonly Upcoming[];
  /** All three attention rows carry the same source line, as the app groups them. */
  source: string;
  attention: readonly string[];
}

/* Split rather than sliced: the app models these as two different rows, not one
   list with a special first element. The hero gets the raised card and a real
   button; the queue behind it gets hairlines and a text link. */
const TASK_COUNT = 18;

/* A source STAMP, not prose, so it keeps its machine format in both locales —
   the same reason the EN side does not write "2 July 2026" either. §7's
   spell-out-the-month rule governs dates we write, not identifiers the app
   emits.

   ISO because that is what the app stores (see MEETING in Hero.tsx), which was
   the owner's ask. `DD_MM_YYYY` was ours, not the product's.

   🚨 THE FORMAT DOES NOT FIX THE ARABIC — .pnhome__actionsrc-date does. This
   read "2026_07_02" under RTL, and switching the separator changes nothing:
   measured in an isolated RTL paragraph, `2026-07-02` after Arabic text renders
   "02-07-2026" and `02_07_2026` renders "2026_07_02". Both reverse.

   The reason is UBA rule W2: a European number is retyped as an ARABIC number
   when the nearest preceding strong character is an Arabic letter. W4 then only
   binds a separator between two EUROPEAN numbers, so the hyphen stops joining
   them, the three groups become three separate runs, and an RTL line lays those
   runs out right to left with each one internally intact — which is exactly the
   "2026 07 02 -> 02 07 2026" signature. The same string ALONE in an RTL
   paragraph is fine, because with no preceding Arabic letter W2 never fires;
   that is why this looked format-shaped and was not.

   The fix is isolation, in the CSS. Kept as one constant so both locales show
   the same stamp. */
const MEETING_DATE = '2026-07-02';

const COPY: Record<Locale, HomeCopy> = {
  en: {
    /* The app picks a greeting from the clock; this is its `morning` one,
       verbatim including the full stop (i18n home.greeting.morning). The
       late-night "Still up, X?" it opened with was the owner's screenshot
       talking, not a default. */
    greeting: 'Good morning, Reem.',
    tasks: (n) => `${n} tasks`,
    upNext: 'Up next',
    needsAttention: 'Needs attention',
    seeAll: 'See all',
    open: 'Open',
    openLink: 'Open →',
    join: 'Join',
    heroRow: {
      time: '10:00 AM',
      when: 'Today',
      title: 'Start of the Week Team Meeting',
      sub: 'No project',
      join: true,
    },
    queueRows: [
      {
        time: '9:00 AM',
        when: 'Tomorrow',
        title: 'Quotation deadline | Coastal Walkway Phase 2',
        sub: 'Coastal Walkway',
      },
      {
        time: '8:00 AM',
        when: 'In 3 days',
        title: 'Site handover | Northgate Logistics Park',
        sub: 'Northgate Logistics',
      },
    ],
    source: 'Warehouse fit-out sync',
    attention: [
      'Send the cleaning contractor a revised quote.',
      'Order cleaning and sanitizing supplies for the new warehouse.',
      'Draft the warehouse readiness plan.',
    ],
  },
  ar: {
    greeting: 'صباح الخير، ريم.',
    /* 18 takes the singular accusative («18 مهمة»), and the digits stay Western
       per §7. The 0/1/2 rule never bites here: the count is fixed at 18. */
    tasks: (n) => `${n} مهمة`,
    upNext: 'التالي',
    needsAttention: 'بحاجة إلى متابعة',
    seeAll: 'عرض الكل',
    open: 'فتح',
    /* Mirrored. In an RTL line the arrow resolves to the visual left, which is
       forward — the same direction "Open →" points in English. */
    openLink: 'فتح ←',
    join: 'انضمام',
    heroRow: {
      time: '10:00 ص',
      when: 'اليوم',
      title: 'اجتماع الفريق لبداية الأسبوع',
      sub: 'دون مشروع',
      join: true,
    },
    queueRows: [
      {
        time: '9:00 ص',
        when: 'غدا',
        title: 'موعد تسليم عرض السعر | ممشى الواجهة البحرية المرحلة 2',
        sub: 'ممشى الواجهة البحرية',
      },
      {
        time: '8:00 ص',
        when: 'بعد 3 أيام',
        /* «نورثجيت اللوجستية» is one indivisible project name — it is what tab
           4's filter selects — so the adjective keeps its ة here too. It read
           «مجمع نورثجيت اللوجستي» against a sub-line saying «اللوجستية» two
           lines below it, which is the same name spelled two ways on one card. */
        title: 'تسليم الموقع | مجمع نورثجيت اللوجستية',
        sub: 'نورثجيت اللوجستية',
      },
    ],
    source: 'اجتماع تجهيز المستودع',
    /* مصدر, not imperatives (§3): a repeated interface-level command forces a
       gender on every reader. These are the same three items ActionsView and
       MinutesView carry, worded identically so the tabs read as one workspace. */
    attention: [
      'إرسال عرض سعر محدث لمقاول النظافة.',
      'طلب مواد النظافة والتعقيم للمستودع.',
      'إعداد خطة جاهزية المستودع.',
    ],
  },
};

export default function HomeView() {
  const t = COPY[useShellLocale()];
  const upNextCount = 1 + t.queueRows.length;
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade(scrollRef);

  return (
    /* Two children on purpose: the scroller and the pop-out are siblings, so the
       floating window stays put while the page behind it scrolls — which is the
       whole point of an always-on-top recorder. */
    <div className="pnhome">
      <div className="pnhome__scroll pn-scroll" ref={scrollRef}>
        <h1 className="pnhome__greet">{t.greeting}</h1>
        <p className="pnhome__count">{t.tasks(TASK_COUNT)}</p>

        <section className="pnhome__section">
          <div className="pnhome__sechead">
            <h2 className="pnhome__sectitle">
              {t.upNext} <span className="pnhome__seccount">· {upNextCount}</span>
            </h2>
            <span className="pnhome__seeall">{t.seeAll}</span>
          </div>

          {/* HeroRow: the next thing due gets the filled card and a real button;
              everything behind it is a quieter queue row with a text link. */}
          <div className="pnhome__hero">
            <span className="pnhome__time">
              <span className="pnhome__time-clock">{t.heroRow.time}</span>
              <span className="pnhome__time-rel">{t.heroRow.when}</span>
            </span>
            <span className="pnhome__herobody">
              <span className="pnhome__herotitle">{t.heroRow.title}</span>
              <span className="pnhome__herosub">
                {t.heroRow.sub}
                {t.heroRow.join && (
                  <>
                    {' · '}
                    <span className="pnhome__join">{t.join}</span>
                  </>
                )}
              </span>
            </span>
            <span className="pnhome__btn">{t.open}</span>
          </div>

          <div className="pnhome__queue">
            {t.queueRows.map((row) => (
              <div className="pnhome__qrow" key={row.title}>
                <span className="pnhome__time">
                  <span className="pnhome__time-clock">{row.time}</span>
                  <span className="pnhome__time-rel">{row.when}</span>
                </span>
                <span className="pnhome__qbody">
                  <span className="pnhome__qtitle">{row.title}</span>
                  <span className="pnhome__qmeta">{row.sub}</span>
                </span>
                <span className="pnhome__link">{t.openLink}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="pnhome__section">
          <div className="pnhome__sechead">
            <h2 className="pnhome__sectitle">
              {t.needsAttention} <span className="pnhome__seccount">· {TASK_COUNT}</span>
            </h2>
            <span className="pnhome__seeall">{t.seeAll}</span>
          </div>

          <div className="pnhome__actions">
            {t.attention.map((text) => (
              <div className="pnhome__action" key={text}>
                <span className="pnhome__check" />
                <span className="pnhome__actionbody">
                  <span className="pnhome__actiontext">{text}</span>
                  <span className="pnhome__actionsrc">
                    {t.source} ·{' '}
                    {/* Its own element so the CSS can isolate it — see
                        MEETING_DATE for why an Arabic line reorders a bare
                        date's groups. */}
                    <span className="pnhome__actionsrc-date">{MEETING_DATE}</span>
                  </span>
                </span>
                {/* The app's no-deadline placeholder is an em dash. It is UI, not
                    copy, so the no-em-dash rule does not reach it. */}
                <span className="pnhome__due">—</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <RecorderPopout />
    </div>
  );
}
